import type { ComponentType } from "react";
import { lazy } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { AppShell } from "./components/AppShell.tsx";
import { ThemeToggle } from "./components/ThemeToggle.tsx";
import { ConfirmHost } from "./components/ConfirmDialog.tsx";
import { Toaster } from "./components/Toaster.tsx";
import { AppearanceProvider } from "./shared/appearance-context.tsx";
import { AppearanceStudioProvider } from "./shared/appearance-studio-context.tsx";
import { AriaPersonaProvider } from "./shared/aria-context.tsx";
import { useAuth } from "./shared/auth-context.tsx";

import { SignInPage } from "./pages/SignInPage.tsx";
import { SignUpPage } from "./pages/SignUpPage.tsx";
import { AuthCallbackPage } from "./pages/AuthCallbackPage.tsx";
import { InviteAcceptPage } from "./pages/InviteAcceptPage.tsx";

// Protected pages load lazily so the entry chunk only pays for auth,
// session bootstrap, and the app shell. `AppShell` stays synchronous — it
// mounts once and stays mounted across nested-route transitions — while
// everything it renders through its `<Outlet />` is fetched on demand,
// behind the `Suspense` and error boundaries that `AppShell` wraps around
// that outlet. Keeping those boundaries inside the shell is what lets the
// nav stay painted while a route chunk is in flight or has failed.
function lazyNamed<T extends ComponentType>(
  loader: () => Promise<T>,
) {
  return lazy(async () => ({ default: await loader() }));
}

const OverviewPage = lazyNamed(
  async () => (await import("./pages/OverviewPage.tsx")).OverviewPage,
);
const PlaygroundPage = lazyNamed(
  async () => (await import("./pages/PlaygroundPage.tsx")).PlaygroundPage,
);
const EvaluationPage = lazyNamed(
  async () => (await import("./pages/EvaluationPage.tsx")).EvaluationPage,
);
const QualityPage = lazyNamed(
  async () => (await import("./pages/QualityPage.tsx")).QualityPage,
);
const KnowledgeImprovementPage = lazyNamed(
  async () => (await import("./pages/KnowledgeImprovementPage.tsx")).KnowledgeImprovementPage,
);
const InboxPage = lazyNamed(
  async () => (await import("./pages/InboxPage.tsx")).InboxPage,
);
const AnalyticsPage = lazyNamed(
  async () => (await import("./pages/AnalyticsPage.tsx")).AnalyticsPage,
);
const AnalyticsPrintPage = lazyNamed(
  async () => (await import("./pages/AnalyticsPrintPage.tsx")).AnalyticsPrintPage,
);
const InstallPage = lazyNamed(
  async () => (await import("./pages/InstallPage.tsx")).InstallPage,
);
const SettingsPage = lazyNamed(
  async () => (await import("./pages/SettingsPage.tsx")).SettingsPage,
);
const AssistantPage = lazyNamed(
  async () => (await import("./pages/AssistantPage.tsx")).AssistantPage,
);
const AriaPage = lazyNamed(
  async () => (await import("./pages/AriaPage.tsx")).AriaPage,
);
const AppearancePage = lazyNamed(
  async () => (await import("./pages/AppearancePage.tsx")).AppearancePage,
);
const EngagementPage = lazyNamed(
  async () => (await import("./pages/EngagementPage.tsx")).EngagementPage,
);
const ReceptionistPage = lazyNamed(
  async () => (await import("./pages/ReceptionistPage.tsx")).ReceptionistPage,
);
const CallsPage = lazyNamed(
  async () => (await import("./pages/CallsPage.tsx")).CallsPage,
);
const BillingSuccessPage = lazyNamed(
  async () => (await import("./pages/BillingSuccessPage.tsx")).BillingSuccessPage,
);
const BillingCanceledPage = lazyNamed(
  async () => (await import("./pages/BillingCanceledPage.tsx")).BillingCanceledPage,
);
const BillingStartPage = lazyNamed(
  async () => (await import("./pages/BillingStartPage.tsx")).BillingStartPage,
);

function Protected({ children }: { children: React.ReactNode }) {
  const { session, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) {
    return (
      <main
        role="status"
        aria-live="polite"
        style={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: "24px",
          color: "var(--ink-soft)",
          fontFamily: "var(--mono)",
          letterSpacing: "0.08em",
        }}
      >
        <span><span className="dot" /> Checking your session…</span>
      </main>
    );
  }
  if (!session) {
    // Capture intended destination so we can deep-link back after signin.
    const next = encodeURIComponent(location.pathname + location.search + location.hash);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/**
 * The unauthenticated routes have no sidebar, so the floating pill is the only
 * way to reach the theme on them. Inside the shell the sidebar footer carries
 * the toggle instead (as the design handoff has it), and rendering both would
 * put two live toggles on screen with the pill sitting over page content.
 */
const UNAUTHENTICATED_PATHS = new Set([
  "/signin",
  "/login",
  "/signup",
  "/auth/callback",
  "/invite/accept",
]);

/** /login -> /signin, query string intact. */
function LoginAlias() {
  const { search } = useLocation();
  return <Navigate to={`/signin${search}`} replace />;
}

export function App() {
  const { pathname } = useLocation();
  return (
    <>
    <Routes>
      {/* Auth */}
      <Route path="/signin" element={<SignInPage />} />
      {/* /login is what people type, and what a single sign-on link gets
          pasted into. Without this it hits the catch-all below, which
          discards the query string -- and for an SSO link the query string
          is the entire message. */}
      <Route path="/login" element={<LoginAlias />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/invite/accept" element={<InviteAcceptPage />} />

      {/* Protected app shell */}
      <Route
        path="/"
        element={
          <Protected>
            <AppearanceProvider>
              <AppearanceStudioProvider>
                <AriaPersonaProvider>
                  {/*
                    The Suspense boundary for lazy route chunks lives inside
                    `AppShell`, wrapping only the outlet — see the comment there.
                    Wrapping the shell at this level would unmount the sidebar and
                    topbar on every first visit to a route.
                  */}
                  <AppShell />
                </AriaPersonaProvider>
              </AppearanceStudioProvider>
            </AppearanceProvider>
          </Protected>
        }
      >
        <Route index element={<Navigate to="/overview" replace />} />
        <Route path="overview" element={<OverviewPage />} />
        <Route path="playground" element={<PlaygroundPage />} />
        <Route path="evaluation" element={<EvaluationPage />} />
        <Route path="quality" element={<QualityPage />} />
        <Route path="knowledge-improvement" element={<KnowledgeImprovementPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="analytics/print" element={<AnalyticsPrintPage />} />
        <Route path="install" element={<InstallPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="aria" element={<AriaPage />} />
        <Route path="assistant" element={<AssistantPage />} />
        <Route path="appearance" element={<AppearancePage />} />
        <Route path="engagement" element={<EngagementPage />} />
        <Route path="receptionist" element={<ReceptionistPage />} />
        <Route path="calls" element={<CallsPage />} />
        <Route path="billing/start" element={<BillingStartPage />} />
        <Route path="billing/success" element={<BillingSuccessPage />} />
        <Route path="billing/canceled" element={<BillingCanceledPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/overview" replace />} />
    </Routes>
    {UNAUTHENTICATED_PATHS.has(pathname) && <ThemeToggle />}
    <Toaster />
    <ConfirmHost />
    </>
  );
}
